import React, { ChangeEvent, FormEvent, useRef, useState } from 'react';
import { NButton, NDialog, NLink } from '@innersource-nn/novoui-react';
import { ModalProps } from 'types/Modal';
import useDomain from '../hooks/useDomain';

export interface DomainModalProps extends ModalProps {
    environmentUri: string;
    domainExists: boolean;
}

export default function DomainModal({
    environmentUri,
    domainExists,
    visible,
    onClose,
}: DomainModalProps) {
    const [configFile, setConfigFile] = useState<File>();
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const { updateDomain } = useDomain(environmentUri);

    const handleFileConfigChange = (event: ChangeEvent<HTMLInputElement>) => {
        const configFile = event.target.files?.[0];
        setConfigFile(configFile);
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setLoading(true);
        if (configFile) {
            const fileReader = new FileReader();
            fileReader.readAsText(configFile, 'UTF-8');

            fileReader.onload = async (readerEvent) => {
                try {
                    // Parse/validate JSON format
                    JSON.parse(readerEvent?.target?.result as string);

                    await updateDomain(readerEvent, onClose);
                } catch (error: any) {
                    setErrorMessage('Invalid JSON: ' + error.message);
                }
                setLoading(false);
            };
        }
    };

    return (
        visible &&
        onClose && (
            <NDialog
                isVisible={visible}
                closeDialog={onClose}
                backgroundClose
                headline={domainExists ? 'Update Domain' : 'Create Domain'}
                size="medium"
                showDividers={false}
            >
                <div>
                    <form className="w-full" onSubmit={handleSubmit}>
                        <div className="pb-16 text-sm text-true-blue">
                            Make sure you have VPC setup before you create a
                            domain. Please take a look at our{' '}
                            <NLink
                                href="https://docs.datahub.data.novonordisk.cloud/User%20documentation/programmatic-access/#sagemaker"
                                className="underline"
                            >
                                SageMaker documentation
                            </NLink>{' '}
                            for more information about VPC.
                        </div>
                        <NButton
                            className="w-full relative border-true-blue border-dotted font-normal"
                            size="large"
                            variant="outline"
                            square
                            leftIcon="upload"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            {configFile?.name || 'Domain configuration file'}
                        </NButton>

                        <input
                            ref={fileInputRef}
                            onChange={handleFileConfigChange}
                            required={!configFile?.name}
                            className="hidden"
                            type="file"
                            accept=".json"
                        />
                        {errorMessage && (
                            <p className="text-lava-red-700 text-sm">
                                {errorMessage}
                            </p>
                        )}
                    </form>
                    <div className="flex w-full justify-end mt-16 gap-x-8">
                        <NButton square variant="outline" onClick={onClose}>
                            Cancel
                        </NButton>
                        <NButton
                            square
                            variant="fill"
                            onClick={handleSubmit}
                            disabled={!configFile}
                            isLoading={loading}
                        >
                            {domainExists ? 'Update' : 'Create'}
                        </NButton>
                    </div>
                </div>
            </NDialog>
        )
    );
}
